import React, { useRef, useEffect, useMemo, useState, useCallback } from "react"
import atlas, { AuthenticationType } from "azure-maps-control"
import "azure-maps-control/dist/atlas.min.css"
import selectedLocation from "./../../../assets/selectedLocation.png"
import mapPlaceholder from "./../../../assets/karrieKoalaMapPlaceholder.svg"
import { apiService } from "../apiService"
import { DataStatus } from "../../../domain/enums"
import { dataStatusesSending } from "../utils"
import appInsightsLogger from "../../logging/appInsightsLogger"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import ColumnContainer from "./Layout/ColumnContainer"
import Typography, { typographyTypes } from "./Typography"
import SkeletonLoader from "./SkeletonLoader"

export interface Marker {
  id: string
  address: string
  lat?: number
  lon?: number
}

const SUBSCRIPTION_KEY = "subscriptionKey" as AuthenticationType
const ANONYMOUS = "anonymous" as AuthenticationType
const NUMBER = "number"
const READY = "ready"

interface StaticMapThumbnailProps {
  marker: Marker
  height?: string
  width?: string
}

const StaticMapThumbnail: React.FC<StaticMapThumbnailProps> = ({
  marker: initialMarker,
  height = "100px",
  width = "132px",
}) => {
  const { cms: cmsContent } = useCMSReady(["publicProfileSlide"])
  const publicProfileText = cmsContent.publicProfileSlide
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<atlas.Map | null>(null)
  const [marker, setMarker] = useState<Marker | null>(null)
  const [geocodingDataStatus, setGeocodingDataStatus] = useState<DataStatus>()
  const subscriptionKey = window._env_.AZURE_MAPS_SUBSCRIPTION_KEY
  const geocodingFetchSending = dataStatusesSending([
    geocodingDataStatus ?? DataStatus.SENDING,
  ])

  // Updates marker on the map instance
  const updateMarker = useMemo(() => {
    return (map: atlas.Map) => {
      map.markers.clear()

      if (
        marker &&
        !!marker.lat &&
        !!marker.lon &&
        typeof marker.lat === NUMBER &&
        typeof marker.lon === NUMBER
      ) {
        const position = [marker.lon, marker.lat] as [number, number]
        const markerHeight = "24px"
        const mapMarker = new atlas.HtmlMarker({
          position: position,
          htmlContent: `<img src="${selectedLocation}" alt="marker" style="width: auto; height: ${markerHeight}; background: transparent;" />`,
        })

        map.setCamera({ center: position, zoom: 12, duration: 0 })
        map.markers.add(mapMarker)
      }
    }
  }, [marker])

  // Initializes the map
  const initMap = useCallback(async () => {
    if (!mapRef.current || !!mapInstanceRef.current) return

    try {
      let mapConfig: atlas.ServiceOptions &
        atlas.StyleOptions &
        (atlas.UserInteractionOptions &
          (atlas.CameraOptions | atlas.CameraBoundsOptions))
      const clientId = window._env_.AZURE_MAPS_CLIENT_ID
      if (clientId) {
        appInsightsLogger.traceInfo(
          "Using managed identity tokens for static map rendering",
        )
        mapConfig = {
          authOptions: {
            authType: ANONYMOUS,
            clientId: clientId, // Use Entra ID client ID
            getToken: (
              resolve: (value?: string) => void,
              reject: (reason?: Error) => void,
            ) => {
              apiService.external.get
                .AZURE_MAPS_ACCESS_TOKEN()
                .then((freshToken) => {
                  if (freshToken.accessToken) {
                    resolve(freshToken.accessToken)
                  } else {
                    appInsightsLogger.traceWarning("No access token received")
                    reject(new Error("No access token available"))
                  }
                })
                .catch((error) => {
                  appInsightsLogger.traceError(
                    "Failed to get Azure Maps token:",
                    error,
                  )
                  reject(error)
                })
            },
          },
          interactive: false,
          // Hide all attributions
          attributionOptions: {
            showCopyright: false,
            showLogo: false,
            showAttribution: false,
            showFeedbackLink: false,
          },
        }
      } else {
        appInsightsLogger.traceInfo(
          "Initializing static map with subscription key authentication",
        )
        if (!subscriptionKey) {
          appInsightsLogger.traceError("No Azure Maps authentication available")
          return
        }
        mapConfig = {
          authOptions: {
            authType: SUBSCRIPTION_KEY,
            subscriptionKey: subscriptionKey,
          },
          interactive: false,
          // Hide all attributions
          attributionOptions: {
            showCopyright: false,
            showLogo: false,
            showAttribution: false,
            showFeedbackLink: false,
          },
        }
      }
      const map = new atlas.Map(mapRef.current, mapConfig)
      mapInstanceRef.current = map

      map.events.add(READY, () => {
        appInsightsLogger.traceInfo("Azure Maps is ready")
        updateMarker(map)
      })
    } catch (error) {
      appInsightsLogger.traceError("Failed to initialize Azure Maps", error)
    }
  }, [updateMarker, subscriptionKey])

  // Adds coordinates to a marker using geocoding
  const addCoords = useCallback(
    async (marker: Marker): Promise<Marker | null> => {
      if (
        marker.lat !== undefined &&
        marker.lon !== undefined &&
        typeof marker.lat === NUMBER &&
        typeof marker.lon === NUMBER
      ) {
        setGeocodingDataStatus(DataStatus.LOADED)
        return marker
      }

      try {
        setGeocodingDataStatus(DataStatus.SENDING)
        const geocodedLocation =
          await apiService.external.get.GEOCODED_LOCATION(marker.address, true)
        setGeocodingDataStatus(DataStatus.LOADED)
        if (geocodedLocation.score > 0.8) {
          return {
            ...marker,
            lat: geocodedLocation.position.lat,
            lon: geocodedLocation.position.lon,
          }
        } else {
          return null
        }
      } catch (error) {
        appInsightsLogger.traceError("StaticMapThumbnail --", error)
        setGeocodingDataStatus(DataStatus.ERROR)
        return null
      }
    },
    [setGeocodingDataStatus],
  )

  // Geocodes initial marker
  useEffect(() => {
    const geocodeMarker = async () => {
      const shouldBeginGeocoding = !geocodingDataStatus
      if (shouldBeginGeocoding) {
        const processedMarker = await addCoords(initialMarker)
        if (processedMarker) {
          setMarker(processedMarker)
        }
      }
    }

    geocodeMarker()
  }, [addCoords, geocodingDataStatus, initialMarker])

  // Initializes the map after geocoding has finished
  useEffect(() => {
    if (!geocodingFetchSending) {
      initMap()
    }
  }, [initMap, geocodingFetchSending])

  const mapComponent =
    !marker || !marker.lat || !marker.lon ? (
      <ColumnContainer styleOverrides="space-y-0 items-center">
        <div
          className="flex items-center justify-center rounded-lg !overflow-hidden bg-gray-100"
          style={{ height, width }}
        >
          <img
            src={mapPlaceholder}
            alt={publicProfileText?.noLocationFound}
            className="h-[90px] w-auto object-contain"
          />
        </div>
        <Typography
          variant={typographyTypes.NORMAL}
          text={publicProfileText?.noLocationFound}
        />
      </ColumnContainer>
    ) : (
      <div
        className="flex items-center justify-center rounded-lg !overflow-hidden"
        style={{ height, width }}
      >
        <div
          ref={mapRef}
          className="w-full map-thumbnail"
          style={{
            height: `calc(${typeof height === "string" ? height : "h-full"} + 100px)`,
            top: "15px",
          }}
        />
      </div>
    )

  if (geocodingFetchSending) {
    return <SkeletonLoader height={height} width={width} />
  } else {
    return mapComponent
  }
}

export default StaticMapThumbnail
